/**
 * AuthContext Module
 * 
 * Provides authentication state and permission checking functionality
 * throughout the application via React Context.
 * 
 * Features:
 * - User authentication state management
 * - Permission checking (single, multiple ANY, multiple ALL)
 * - Role checking
 * - Super Admin bypass logic
 * - Memoized helpers for performance
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import apiClient from '../api/apiClient';
import authService from '../services/authService';
import { ROLES } from '../config/permissions';
import { discardOnboardingSession } from '../utils/investorOnboardingPersistence';

const AuthContext = createContext(null);

function normalizePermissionKey(permission) {
  if (!permission) return '';
  if (typeof permission === 'string') return permission.trim();
  if (typeof permission === 'object') {
    return String(
      permission.key ??
        permission.code ??
        permission.name ??
        permission.permission ??
        ''
    ).trim();
  }
  return '';
}

function normalizePermissions(permissions) {
  if (!permissions) return [];
  if (typeof permissions === 'string') {
    const key = permissions.trim();
    return key ? [key] : [];
  }
  if (!Array.isArray(permissions)) return [];
  return permissions.map(normalizePermissionKey).filter(Boolean);
}

function normalizeRoleKey(role) {
  if (!role) return '';
  if (typeof role === 'string') return role.trim();
  if (typeof role === 'object') {
    return String(role.name ?? role.label ?? role.role ?? role.key ?? '').trim();
  }
  return '';
}

function normalizeRoles(roles) {
  if (!roles) return [];
  if (typeof roles === 'string') {
    const key = roles.trim();
    return key ? [key] : [];
  }
  if (!Array.isArray(roles)) return [];
  return roles.map(normalizeRoleKey).filter(Boolean);
}

function permissionsInclude(permissions, permission) {
  if (!permission) return true;
  const target = String(permission).trim();
  if (!target) return true;
  return normalizePermissions(permissions).includes(target);
}

/**
 * AuthProvider component
 * Wraps the app to provide auth state and permission helpers
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element}
 */
export function AuthProvider({ children }) {
  const [auth, setAuth] = useState(() => apiClient.getUserData());

  /**
   * Load auth data from localStorage
   * @returns {void}
   */
  const loadFromStorage = useCallback(() => {
    setAuth(apiClient.getUserData());
  }, []);

  /**
   * Call after successful login; pass response.data (e.g. { user })
   * @param {Object} data - Login response data containing user, permissions, roles
   * @returns {void}
   */
  const login = useCallback((data) => {
    if (data?.user) {
      apiClient.setAuthData(data);
      setAuth(apiClient.getUserData());
    }
  }, []);

  /**
   * Call backend logout (invalidates session), then clear local auth
   * @returns {Promise<void>}
   */
  const logout = useCallback(async () => {
    await discardOnboardingSession({ cancelRemote: true });
    await authService.logout();
    setAuth(null);
  }, []);

  useEffect(() => {
    loadFromStorage();
  }, [loadFromStorage]);

  /**
   * Memoize permission and role checks for performance
   * Only recomputes when auth state changes
   * @returns {Object} Object containing user, permissions, roles, and helper functions
   */
  const authHelpers = useMemo(() => {
    // Support both shapes: stored { permissions, roles } or raw API { user: { permissions, roles } }
    const permissions = normalizePermissions(
      auth?.permissions ?? auth?.user?.permissions ?? []
    );
    const roles = normalizeRoles(auth?.roles ?? auth?.user?.roles ?? []);
    const user = auth?.user ?? null; // Object | null

    // Super Admin bypasses all permission checks.
    const isSuperAdmin = roles.includes(ROLES.SUPER_ADMIN);

    /**
     * Check if user has a specific permission
     * Super Admin always has access
     * @param {string} permission - Permission string to check (e.g., 'RBAC_USER_MANAGEMENT_VIEW')
     * @returns {boolean} - True if user has permission or is Super Admin
     */
    const hasPermission = (permission) => {
      if (!permission) return true; // No permission required
      if (isSuperAdmin) return true; // Super Admin has all permissions
      return permissions.includes(permission);
    };

    /**
     * Check if user has ANY of the provided permissions
     * Super Admin always has access
     * @param {string[]} permissionArray - Array of permission strings
     * @returns {boolean} - True if user has at least one permission or is Super Admin
     */
    const hasAnyPermission = (permissionArray) => {
      if (!permissionArray || permissionArray.length === 0) return true;
      if (isSuperAdmin) return true;
      return permissionArray.some((perm) => permissions.includes(perm));
    };

    /**
     * Check if user has ALL of the provided permissions
     * Super Admin always has access
     * @param {string[]} permissionArray - Array of permission strings
     * @returns {boolean} - True if user has all permissions or is Super Admin
     */
    const hasAllPermissions = (permissionArray) => {
      if (!permissionArray || permissionArray.length === 0) return true;
      if (isSuperAdmin) return true;
      return permissionArray.every((perm) => permissions.includes(perm));
    };

    /**
     * Check if user has a specific role
     * @param {string} role - Role string to check (e.g., 'Super Admin')
     * @returns {boolean} - True if user has the role
     */
    const hasRole = (role) => {
      if (!role) return false;
      return roles.includes(role);
    };

    /**
     * Check if user has ANY of the provided roles
     * @param {string[]} roleArray - Array of role strings
     * @returns {boolean} - True if user has at least one role
     */
    const hasAnyRole = (roleArray) => {
      if (!roleArray || roleArray.length === 0) return false;
      return roleArray.some((role) => roles.includes(role));
    };

    /**
     * Check permission without Super Admin bypass — for actions that require an
     * explicit grant even for Super Admin (e.g. ORDERS_CHANGE_PARTNER_PRICE).
     * @param {string} permission
     * @returns {boolean}
     */
    const hasExplicitPermission = (permission) => permissionsInclude(permissions, permission);

    /**
     * @returns {Object} authHelpers
     * @property {Object|null} user - Current user object
     * @property {string[]} permissions - Array of permission strings
     * @property {string[]} roles - Array of role strings
     * @property {boolean} isSuperAdmin - Is user Super Admin
     * @property {boolean} isAuthenticated - Is user authenticated
     * @property {Function} hasPermission - Check single permission
     * @property {Function} hasAnyPermission - Check if user has ANY permission from array
     * @property {Function} hasAllPermissions - Check if user has ALL permissions from array
     * @property {Function} hasRole - Check single role
     * @property {Function} hasAnyRole - Check if user has ANY role from array
     */
    return {
      user,
      permissions,
      roles,
      isSuperAdmin,
      isAuthenticated: !!auth,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      hasRole,
      hasAnyRole,
      hasExplicitPermission,
    };
  }, [auth]);

  /**
   * Final context value combining authHelpers with auth actions
   * @type {Object}
   */
  const value = {
    ...authHelpers,
    login,
    logout,
    loadFromStorage,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth context
 * Provides user, permissions, roles, and all helper functions
 * @returns {Object} Auth context value containing user, permissions, roles, and helper functions
 * @throws {Error} If used outside AuthProvider
 */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}

/**
 * Hook: Check if user has a specific permission
 * Super Admin automatically has access
 * @param {string} permission - Permission string to check (e.g., 'RBAC_USER_MANAGEMENT_VIEW')
 * @returns {boolean} - True if user has permission or is Super Admin
 */
export function usePermission(permission) {
  const { hasPermission } = useAuth();
  return hasPermission(permission);
}

/**
 * Hook: Check if user has ANY of the provided permissions
 * Super Admin automatically has access
 * @param {string[]} permissionArray - Array of permission strings
 * @returns {boolean} - True if user has at least one permission or is Super Admin
 */
export function useAnyPermission(permissionArray) {
  const { hasAnyPermission } = useAuth();
  return hasAnyPermission(permissionArray);
}

/**
 * Hook: Check if user has ALL of the provided permissions
 * Super Admin automatically has access
 * @param {string[]} permissionArray - Array of permission strings
 * @returns {boolean} - True if user has all permissions or is Super Admin
 */
export function useAllPermissions(permissionArray) {
  const { hasAllPermissions } = useAuth();
  return hasAllPermissions(permissionArray);
}

/**
 * Hook: Check if user has a specific role
 * @param {string} role - Role string to check (e.g., 'Super Admin')
 * @returns {boolean} - True if user has the role
 */
export function useHasRole(role) {
  const { hasRole } = useAuth();
  return hasRole(role);
}

/**
 * Hook: Check if user has ANY of the provided roles
 * @param {string[]} roleArray - Array of role strings
 * @returns {boolean} - True if user has at least one role
 */
export function useHasAnyRole(roleArray) {
  const { hasAnyRole } = useAuth();
  return hasAnyRole(roleArray);
}

/**
 * Hook: Check if user is Super Admin
 * @returns {boolean} - True if user has Super Admin role
 */
export function useIsSuperAdmin() {
  const { isSuperAdmin } = useAuth();
  return isSuperAdmin;
}

/**
 * Hook: Check explicit permission without Super Admin bypass.
 * @param {string} permission
 * @returns {boolean}
 */
export function useExplicitPermission(permission) {
  const { hasExplicitPermission } = useAuth();
  return hasExplicitPermission(permission);
}
